import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subscription as RxSub } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { IClassesEntityService } from '../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../injection-tokens/entity-service-tokens';
import { ResourceMasterDataService } from '../../services/resource-master-data.service';
import { Log } from '../../services/logger.service';

export interface TemplateSubAddedItem {
  id: string;
  subscriptionId: string;
  displayName: string;
  location: string;
  coresLimit?: number;
  coresCurrentUsage?: number;
  pendingCores?: number;
  quotaCheckedAt?: string;
}

export interface TemplateSubBulkAddDialogResult {
  added: TemplateSubAddedItem[];
}

interface AddResult {
  subId: string;
  ok: boolean;
  message: string;
  item?: TemplateSubAddedItem;
}

@Component({
  selector: 'app-template-sub-bulk-add-dialog',
  template: `
    <div class="tsba-dialog">
      <div mat-dialog-title class="tsba-dialog-title">
        <div class="tsba-title-text">
          <mat-icon>dns</mat-icon>
          <span>Add Template Subscriptions</span>
        </div>
        <button mat-icon-button mat-dialog-close matTooltip="Close" [disabled]="isProcessing">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <mat-dialog-content class="tsba-dialog-content">

        <!-- ── Input phase ── -->
        <ng-container *ngIf="!showResults">
          <p class="tsba-desc">
            Select a shared Azure region, then paste one or more Azure subscription IDs below.
            Display names are <strong>auto-resolved from Azure</strong>.
            Accepts any mix of newlines, commas, semicolons, or tabs as separators.
          </p>

          <!-- Location autocomplete -->
          <mat-form-field appearance="outline" class="tsba-full-width">
            <mat-label>Azure Region (shared for all subscriptions)</mat-label>
            <input matInput
              [formControl]="locationCtrl"
              [matAutocomplete]="autoRegion"
              placeholder="Search region… e.g. Central India"
              (focus)="locationCtrl.setValue(locationCtrl.value)"
              [disabled]="isProcessing" />
            <mat-icon matSuffix>public</mat-icon>
            <mat-hint>Select the Azure region where these subscriptions will be used</mat-hint>
            <mat-autocomplete #autoRegion="matAutocomplete"
              autoActiveFirstOption
              (optionSelected)="onRegionSelected($event)">
              <mat-option *ngFor="let r of filteredRegions$ | async" [value]="r.id">
                {{ r.name }}<span class="tsba-region-id">&nbsp;·&nbsp;{{ r.id }}</span>
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <!-- IDs textarea -->
          <mat-form-field appearance="outline" class="tsba-full-width">
            <mat-label>Subscription IDs</mat-label>
            <textarea matInput [formControl]="idsCtrl"
              rows="6"
              style="resize: vertical;"
              [placeholder]="idsPlaceholder"
              [disabled]="isProcessing">
            </textarea>
            <mat-hint>{{ parsedCount }} subscription ID(s) detected</mat-hint>
            <mat-error *ngIf="idsCtrl.hasError('required')">At least one subscription ID is required</mat-error>
          </mat-form-field>

          <div *ngIf="isProcessing" class="tsba-progress-row">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            <span class="tsba-progress-label">Processing {{ currentIndex }} of {{ totalToProcess }}…</span>
          </div>
        </ng-container>

        <!-- ── Results phase ── -->
        <ng-container *ngIf="showResults">
          <div class="tsba-results-summary">
            <div class="tsba-summary-chip tsba-chip-ok">
              <mat-icon>check_circle</mat-icon>
              {{ addedCount }} added
            </div>
            <div class="tsba-summary-chip tsba-chip-fail" *ngIf="failedCount > 0">
              <mat-icon>cancel</mat-icon>
              {{ failedCount }} failed
            </div>
          </div>

          <div class="tsba-results-list">
            <div *ngFor="let r of results"
              [class]="'tsba-result-row ' + (r.ok ? 'ok' : 'fail')">
              <mat-icon class="tsba-result-icon">{{ r.ok ? 'check_circle' : 'cancel' }}</mat-icon>
              <div class="tsba-result-body">
                <span class="tsba-result-name">{{ r.item?.displayName || r.subId }}</span>
                <span class="tsba-result-subid">{{ r.subId }}</span>
                <span *ngIf="!r.ok" class="tsba-result-error">{{ r.message }}</span>
              </div>
            </div>
          </div>
        </ng-container>

      </mat-dialog-content>

      <mat-dialog-actions class="tsba-dialog-actions">
        <ng-container *ngIf="!showResults">
          <button mat-button [mat-dialog-close]="null" [disabled]="isProcessing">Cancel</button>
          <button mat-flat-button color="primary"
            [disabled]="parsedCount === 0 || !selectedLocationId || isProcessing"
            (click)="runAdd()">
            <mat-spinner *ngIf="isProcessing" diameter="18"
              style="display:inline-block;margin-right:6px;vertical-align:middle;"></mat-spinner>
            <mat-icon *ngIf="!isProcessing">playlist_add_check</mat-icon>
            {{ isProcessing
              ? ('Adding ' + currentIndex + '/' + totalToProcess + '…')
              : ('Add ' + (parsedCount > 1 ? parsedCount + ' Subscriptions' : 'Subscription')) }}
          </button>
        </ng-container>
        <ng-container *ngIf="showResults">
          <button mat-flat-button (click)="reset()" *ngIf="hasFailures">
            <mat-icon>refresh</mat-icon> Retry Failed
          </button>
          <button mat-flat-button color="primary" [mat-dialog-close]="closeResult">
            <mat-icon>done</mat-icon> Done
          </button>
        </ng-container>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .tsba-dialog { min-width: 540px; max-width: 720px; }
    .tsba-dialog-title {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 16px 0;
    }
    .tsba-title-text {
      display: flex; align-items: center; gap: 8px;
      font-size: 1.1rem; font-weight: 600;
    }
    .tsba-title-text mat-icon { color: #1565c0; }
    .tsba-dialog-content { padding: 12px 16px; min-height: 200px; }
    .tsba-desc { margin: 0 0 16px; font-size: 0.875rem; color: #555; line-height: 1.5; }
    .tsba-full-width { width: 100%; }
    .tsba-region-id { font-size: 11px; color: #888; font-family: monospace; }
    .tsba-progress-row { margin-top: 12px; }
    .tsba-progress-label { font-size: 0.8rem; color: #777; margin-top: 4px; display: block; }
    .tsba-dialog-actions {
      padding: 8px 16px 16px;
      display: flex; justify-content: flex-end; gap: 8px;
    }
    /* Results */
    .tsba-results-summary { display: flex; gap: 12px; margin-bottom: 16px; }
    .tsba-summary-chip {
      display: flex; align-items: center; gap: 6px;
      padding: 6px 14px; border-radius: 20px;
      font-size: 0.875rem; font-weight: 600;
    }
    .tsba-chip-ok   { background: #e8f5e9; color: #2e7d32; }
    .tsba-chip-fail { background: #ffebee; color: #c62828; }
    .tsba-results-list { display: flex; flex-direction: column; gap: 8px; }
    .tsba-result-row {
      display: flex; align-items: flex-start; gap: 10px;
      padding: 10px 12px; border-radius: 8px;
    }
    .tsba-result-row.ok   { background: #f1f8e9; }
    .tsba-result-row.fail { background: #fff8f8; }
    .tsba-result-icon { margin-top: 2px; flex-shrink: 0; }
    .tsba-result-row.ok   .tsba-result-icon { color: #43a047; }
    .tsba-result-row.fail .tsba-result-icon { color: #e53935; }
    .tsba-result-body { display: flex; flex-direction: column; gap: 2px; }
    .tsba-result-name   { font-weight: 500; font-size: 0.9rem; }
    .tsba-result-subid  { font-size: 0.78rem; font-family: monospace; color: #777; }
    .tsba-result-error  { font-size: 0.78rem; color: #c62828; }
    @media (max-width: 600px) { .tsba-dialog { min-width: unset; width: 100%; } }
  `],
})
export class TemplateSubBulkAddDialogComponent implements OnInit, OnDestroy {
  idsCtrl = new FormControl('', Validators.required);
  /** Text shown in the location autocomplete input (human-readable name). */
  locationCtrl = new FormControl('');
  /** Canonical Azure region ID stored separately (e.g. 'centralindia'). */
  locationIdCtrl = new FormControl('', Validators.required);

  filteredRegions$!: Observable<{ id: string; name: string }[]>;

  isProcessing = false;
  showResults = false;
  results: AddResult[] = [];
  currentIndex = 0;
  totalToProcess = 0;

  readonly idsPlaceholder =
    'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\nyyyyyyyy-yyyy-yyyy-yyyy-yyyyyyyyyyyy';

  private readonly allRegions: { id: string; name: string }[] = [];
  private readonly logger = Log.create(TemplateSubBulkAddDialogComponent);

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: {
      existingSubscriptions: Array<{ subscriptionId: string; location: string }>;
    },
    private readonly dialogRef: MatDialogRef<TemplateSubBulkAddDialogComponent>,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly masterData: ResourceMasterDataService,
  ) {
    this.allRegions =
      this.masterData.get<{ id: string; name: string }[]>('cloud.azure.regions') ?? [];
  }

  ngOnInit(): void {
    this.filteredRegions$ = this.locationCtrl.valueChanges.pipe(
      startWith(''),
      map(term => this._filterRegions(term ?? '')),
    );
  }

  ngOnDestroy(): void {}

  /** Canonical region ID selected in the autocomplete. */
  get selectedLocationId(): string {
    return this.locationIdCtrl.value ?? '';
  }

  /** Number of unique valid-looking IDs in the textarea. */
  get parsedCount(): number {
    return this.parseIds().length;
  }

  get addedCount(): number { return this.results.filter(r => r.ok).length; }
  get failedCount(): number { return this.results.filter(r => !r.ok).length; }
  get hasFailures(): boolean { return this.results.some(r => !r.ok); }

  get closeResult(): TemplateSubBulkAddDialogResult {
    return { added: this.results.filter(r => r.ok).map(r => r.item!) };
  }

  onRegionSelected(event: any): void {
    const selectedId: string = event.option.value;
    const region = this.allRegions.find(r => r.id === selectedId);
    this.locationIdCtrl.setValue(selectedId);
    this.locationCtrl.setValue(region ? region.name : selectedId, { emitEvent: false });
  }

  private _filterRegions(term: string): { id: string; name: string }[] {
    const lower = term.toLowerCase().trim();
    if (!lower) return this.allRegions;
    return this.allRegions.filter(
      r => r.name.toLowerCase().includes(lower) || r.id.toLowerCase().includes(lower),
    );
  }

  private parseIds(): string[] {
    const raw = this.idsCtrl.value ?? '';
    return [...new Set(
      raw.split(/[\n,;\t\s]+/)
        .map(s => s.trim())
        .filter(s => s.length >= 32),
    )];
  }

  async runAdd(): Promise<void> {
    const location = this.selectedLocationId;
    if (!location) return;
    const ids = this.parseIds();
    if (!ids.length) return;
    const normalizedLocation = location.trim().toLowerCase().replace(/\s+/g, '');

    this.isProcessing = true;
    this.results = [];
    this.currentIndex = 0;
    this.totalToProcess = ids.length;

    for (const subId of ids) {
      this.currentIndex++;

      const comboAlreadyExists = (this.data.existingSubscriptions ?? []).some(existing => {
        const normalizedExistingSubId = (existing?.subscriptionId ?? '').trim().toLowerCase();
        const normalizedExistingLocation = (existing?.location ?? '').trim().toLowerCase().replace(/\s+/g, '');
        return normalizedExistingSubId === subId.toLowerCase() && normalizedExistingLocation === normalizedLocation;
      });
      if (comboAlreadyExists) {
        this.results.push({ subId, ok: false, message: 'Already exists in org template subscriptions' });
        continue;
      }

      try {
        // Resolve display name from Azure first (best-effort — fallback to subId)
        let displayName = '';
        try {
          const lookup = await this.classService.lookupTemplateSubscription(subId).toPromise();
          displayName = lookup?.displayName ?? '';
        } catch {
          // Lookup failed — proceed without display name; backend will assign one
        }

        const created: any = await this.classService.createTemplateSubscription({
          SubscriptionId: subId,
          Location: location,
          SubscriptionDisplayName: displayName,
        }).toPromise();

        const row = created?.subscription ?? created;
        const resolvedName =
          (row?.SubscriptionDisplayName ?? row?.subscriptionDisplayName ?? displayName) || subId;

        this.results.push({
          subId,
          ok: true,
          message: resolvedName,
          item: {
            id:               row?.Id                  ?? row?.id                  ?? '',
            subscriptionId:   row?.SubscriptionId      ?? row?.subscriptionId      ?? subId,
            displayName:      resolvedName,
            location:         row?.Location            ?? row?.location            ?? location,
            coresLimit:       row?.CoresLimit          ?? row?.coresLimit,
            coresCurrentUsage:row?.CoresCurrentUsage   ?? row?.coresCurrentUsage,
            pendingCores:     row?.PendingCores        ?? row?.pendingCores,
            quotaCheckedAt:   row?.QuotaCheckedAt      ?? row?.quotaCheckedAt,
          },
        });
      } catch (err: any) {
        const msg = err?.error?.message ?? err?.message ?? 'Failed to add';
        this.results.push({ subId, ok: false, message: msg });
        this.logger.e(`Failed to add template subscription ${subId}:`, err);
      }
    }

    this.isProcessing = false;
    this.showResults = true;
  }

  reset(): void {
    const failedIds = this.results.filter(r => !r.ok).map(r => r.subId);
    this.idsCtrl.setValue(failedIds.join('\n'));
    this.showResults = false;
    this.results = [];
  }
}
